import { useMemo, useState } from "react";
import SearchableSelect from "@/components/SearchableSelect";
import { computeWeeksOfYear, fmtShortDate } from "./utils";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function PettyCashExportDialog({ open, onOpenChange, currentProjectId = "", currentProjectNames = [], currentUserIds = "", currentUserNames = [] }) {
  const [expYear, setExpYear] = useState(new Date().getFullYear());
  // 149-cw-r182: default range = setahun penuh — dulunya W01-W12 sehingga data
  // di minggu > 12 (mis. Head Office W030+) ter-export KOSONG.
  const [expFrom, setExpFrom] = useState(1);
  const [expTo, setExpTo] = useState(53);
  const [expFormat, setExpFormat] = useState("xlsx");
  const [expBusy, setExpBusy] = useState(false);
  // 149-cw-r174: mode export — bulk transfer (existing) vs rekapitulasi.
  // 149-cw-r176: mode "detail" — 1 baris per item pengeluaran (bukan summary).
  const [expMode, setExpMode] = useState("transfer"); // transfer | project | detail
  // 149-cw-r188: filter project TIDAK diulang di sini — export mengikuti filter
  // project halaman Weekly Petty Cash (currentProjectId). Kosong = semua project.

  // 149-cw-r181: opsi week menampilkan periode tanggal (sama dgn form pengajuan baru).
  const expWeeks = useMemo(() => computeWeeksOfYear(expYear), [expYear]);
  // 149-cw-r196: label kompak (tanpa tahun berulang) supaya terbaca penuh di
  // trigger — tahun sudah jelas dari selector Tahun di sebelahnya.
  const weekOptions = useMemo(
    () => expWeeks.map((w) => ({
      value: String(w.no),
      label: `W${String(w.no).padStart(2, "0")} · ${fmtShortDate(w.start).replace(` ${expYear}`, "")} – ${fmtShortDate(w.end).replace(` ${expYear}`, "")}`,
    })),
    [expWeeks, expYear]
  );

  const doExport = async () => {
    if (Number(expFrom) > Number(expTo)) { toast.error("Week 'From' harus <= 'To'"); return; }
    const wf = `${expYear}-W${String(expFrom).padStart(2, "0")}`;
    const wt = `${expYear}-W${String(expTo).padStart(2, "0")}`;
    const pids = currentProjectId ? `&project_ids=${currentProjectId}` : "";
    // 2026-06-10: filter staff halaman ikut ter-export.
    const uids = currentUserIds ? `&user_ids=${currentUserIds}` : "";
    setExpBusy(true);
    try {
      // Use axios (dual-mode auth) instead of reading ens_token from localStorage.
      // Rekap selalu XLSX (agregasi); bulk transfer mengikuti pilihan format.
      const url = expMode === "transfer"
        ? `/petty-cash/pending-transfers.${expFormat}?week_from=${wf}&week_to=${wt}${pids}${uids}`
        : expMode === "detail"
        ? `/petty-cash/recap/export-items.xlsx?week_from=${wf}&week_to=${wt}${pids}${uids}`
        : `/petty-cash/recap/export.xlsx?group_by=${expMode}&week_from=${wf}&week_to=${wt}${pids}${uids}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = res.data;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = expMode === "transfer"
        ? `petty_cash_${wf}_to_${wt}.${expFormat}`
        : expMode === "detail"
        ? `detail_petty_cash_per_item_${wf}_sd_${wt}.xlsx`
        : `rekap_petty_cash_per_${expMode}_${wf}_sd_${wt}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      onOpenChange(false);
      toast.success("Ekspor selesai");
    } catch { toast.error("Gagal export"); }
    finally { setExpBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-xl w-full" data-testid="dlg-bulk-export">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Export Petty Cash</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* 149-cw-r174: mode export — Rekap per Project vs Bulk Transfer.
              2026-06-10 (user request): "Rekap per Periode" DIHAPUS — mode lain
              sudah mencantumkan periode (week range) yang dipilih. */}
          <div>
            <label className="overline text-slate-600 text-[10px] block mb-1">Jenis Export</label>
            <div className="grid grid-cols-3 border border-slate-300 w-full">
              {[
                { v: "transfer", l: "Bulk Transfer" },
                { v: "project",  l: "Rekap per Project" },
                { v: "detail",   l: "Detail per Item" },
              ].map((m, i) => (
                <button
                  key={m.v}
                  type="button"
                  data-testid={`exp-mode-${m.v}`}
                  onClick={() => setExpMode(m.v)}
                  className={`h-8 px-2 text-[10px] uppercase tracking-wider font-semibold ${i > 0 ? "border-l border-slate-300" : ""} ${expMode === m.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >{m.l}</button>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-1 italic">
              {expMode === "transfer"
                ? "Daftar transfer untuk internet banking (per baris = pengajuan)."
                : expMode === "project"
                ? "Rekapitulasi: 1 baris per project — jml pengajuan, total diajukan, ditransfer, terverifikasi."
                : "Detail: 1 baris per item pengeluaran — nama item, qty, harga satuan, subtotal, status."}
            </div>
          </div>
          <div className="text-[11px] text-slate-500">Pilih rentang minggu yang ingin di-export. Contoh: <span className="font-semibold">Week 1 sampai Week 12</span>.</div>
          {/* 149-cw-r188: filter project tidak diulang — export mengikuti filter
              halaman Weekly Petty Cash. Ditampilkan sbg info saja. */}
          <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-[11px]" data-testid="exp-project-follow">
            <span className="text-slate-500">Filter project mengikuti halaman: </span>
            {/* 149-cw-r196 (user request): jabarkan NAMA semua project yang
                dipilih, bukan hanya jumlah ("2 project dipilih"). */}
            {currentProjectNames.length === 0 ? (
              <span className="font-semibold text-slate-800">Semua Project</span>
            ) : (
              <span className="font-semibold text-slate-800" data-testid="exp-project-names">{currentProjectNames.join(" · ")}</span>
            )}
          </div>
          {/* 2026-06-10: filter staff halaman juga diikuti export. */}
          <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-[11px]" data-testid="exp-user-follow">
            <span className="text-slate-500">Filter staff mengikuti halaman: </span>
            {currentUserNames.length === 0 ? (
              <span className="font-semibold text-slate-800">Semua Staff</span>
            ) : (
              <span className="font-semibold text-slate-800" data-testid="exp-user-names">{currentUserNames.join(" · ")}</span>
            )}
          </div>
          <div className="grid grid-cols-[110px_1fr_1fr] gap-3">
            <div>
              <label className="overline text-slate-600 text-[10px] block mb-1">Tahun</label>
              <input
                data-testid="exp-year"
                type="number" min="2020" max="2099"
                value={expYear}
                onChange={(e) => setExpYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                className="w-full h-9 px-2 border border-slate-300 text-sm tabular-nums"
              />
            </div>
            <div>
              <label className="overline text-slate-600 text-[10px] block mb-1">From Week</label>
              {/* 149-cw-r181: SearchableSelect dgn periode tanggal per minggu. */}
              <SearchableSelect
                testid="exp-from"
                value={String(expFrom)}
                onChange={(v) => setExpFrom(parseInt(v, 10) || 1)}
                placeholder="Pilih minggu…"
                searchPlaceholder="Ketik nomor minggu / tanggal…"
                options={weekOptions}
              />
            </div>
            <div>
              <label className="overline text-slate-600 text-[10px] block mb-1">To Week</label>
              <SearchableSelect
                testid="exp-to"
                value={String(expTo)}
                onChange={(v) => setExpTo(parseInt(v, 10) || 1)}
                placeholder="Pilih minggu…"
                searchPlaceholder="Ketik nomor minggu / tanggal…"
                options={weekOptions}
              />
            </div>
          </div>
          {expMode === "transfer" && (
          <div>
            <label className="overline text-slate-600 text-[10px] block mb-1">Format</label>
            <div className="inline-flex border border-slate-300">
              <button
                type="button"
                data-testid="exp-fmt-xlsx"
                onClick={() => setExpFormat("xlsx")}
                className={`h-8 px-3 text-[11px] uppercase tracking-wider font-semibold ${expFormat === "xlsx" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >XLSX</button>
              <button
                type="button"
                data-testid="exp-fmt-csv"
                onClick={() => setExpFormat("csv")}
                className={`h-8 px-3 text-[11px] uppercase tracking-wider font-semibold border-l border-slate-300 ${expFormat === "csv" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >CSV</button>
            </div>
          </div>
          )}
          <div className="bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-600 space-y-1">
            <div>
              <span className="font-semibold">Preview:</span>{" "}
              <span className="tabular-nums font-mono">{expYear}-W{String(expFrom).padStart(2, "0")}</span>
              {" → "}
              <span className="tabular-nums font-mono">{expYear}-W{String(expTo).padStart(2, "0")}</span>
            </div>
            {(() => {
              const weeks = computeWeeksOfYear(expYear);
              const wf = weeks.find((w) => w.no === Number(expFrom));
              const wt = weeks.find((w) => w.no === Number(expTo));
              if (!wf || !wt) return null;
              return (
                <div className="text-slate-500" data-testid="exp-date-range">
                  Periode:{" "}
                  <span className="font-semibold text-slate-700 tabular-nums">{fmtShortDate(wf.start)}</span>
                  {" → "}
                  <span className="font-semibold text-slate-700 tabular-nums">{fmtShortDate(wt.end)}</span>
                </div>
              );
            })()}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Batal</Button>
          <Button type="button" data-testid="btn-do-export" onClick={doExport} disabled={expBusy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white">
            <FileDown size={12} className="mr-1" /> {expBusy ? "Menyiapkan…" : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
